import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ChevronLeft, ChevronRight } from "lucide-react";
import {
  format,
  addMonths,
  subMonths,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  isSameMonth,
  isToday,
  addWeeks,
  startOfWeek,
  endOfWeek,
  getDay,
  addDays,
  isSameDay,
  parseISO,
} from "date-fns";
import { cn } from "@/lib/utils";
import { Id } from "../../../../../convex/_generated/dataModel";

interface Project {
  _id: Id<"locationProjects">;
  name: string;
  description?: string;
  status: string;
  startDate: string;
  dueDate: string;
  locationName: string;
  locationAddress: string;
  businessName: string;
  assignedToNames?: string[];
  assignedSubcontractorNames?: string[];
}

interface ProjectCalendarViewProps {
  projects: Project[];
  onViewProject: (project: Project) => void;
  onEditProject: (project: Project) => void;
}

// Get status color for project events (border-left style like My Calendar)
const getStatusBorderColor = (status: string) => {
  switch (status) {
    case "Scheduled":
      return "bg-blue-100 border-blue-500";
    case "In Progress":
      return "bg-yellow-100 border-yellow-500";
    case "Completed":
      return "bg-green-100 border-green-500";
    default:
      return "bg-gray-100 border-gray-500";
  }
};

export const ProjectCalendarView: React.FC<ProjectCalendarViewProps> = ({
  projects,
  onViewProject,
  onEditProject,
}) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState<"month" | "week">("month");

  // Navigation functions
  const nextPeriod = () => {
    if (view === "month") {
      setCurrentDate(addMonths(currentDate, 1));
    } else {
      setCurrentDate(addWeeks(currentDate, 1));
    }
  };

  const prevPeriod = () => {
    if (view === "month") {
      setCurrentDate(subMonths(currentDate, 1));
    } else {
      setCurrentDate(addWeeks(currentDate, -1));
    }
  };

  // Get days to display based on current view
  const getDaysToDisplay = () => {
    if (view === "month") {
      const start = startOfMonth(currentDate);
      const end = endOfMonth(currentDate);
      return eachDayOfInterval({ start, end });
    } else {
      const start = startOfWeek(currentDate);
      const end = endOfWeek(currentDate);
      return eachDayOfInterval({ start, end });
    }
  };

  // Get projects for a specific day
  const getProjectsForDay = (day: Date) => {
    return projects.filter((project) => {
      try {
        const startDate = parseISO(project.startDate);
        return isSameDay(startDate, day);
      } catch {
        return false;
      }
    });
  };

  // Render calendar header
  const renderHeader = () => {
    return (
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <Button variant="outline" size="icon" onClick={prevPeriod}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <h2 className="min-w-[200px] text-center text-xl font-bold">
            {view === "month"
              ? format(currentDate, "MMMM yyyy")
              : `Week of ${format(startOfWeek(currentDate), "MMM d")} - ${format(endOfWeek(currentDate), "MMM d, yyyy")}`}
          </h2>
          <Button variant="outline" size="icon" onClick={nextPeriod}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>

        <div className="flex items-center space-x-2">
          <Button
            variant={view === "month" ? "default" : "outline"}
            onClick={() => setView("month")}
          >
            Month
          </Button>
          <Button variant={view === "week" ? "default" : "outline"} onClick={() => setView("week")}>
            Week
          </Button>
        </div>
      </div>
    );
  };

  // Render header row with day names
  const renderHeaderRow = () => {
    return (
      <div className="grid grid-cols-7 bg-gray-100 font-semibold dark:bg-gray-800">
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
          <div key={day} className="p-2 text-center">
            {day}
          </div>
        ))}
      </div>
    );
  };

  // Render a single day cell (matching My Calendar DayCell component)
  const renderDayCell = (day: Date, isCurrentMonth: boolean) => {
    const dayProjects = getProjectsForDay(day);
    const isCurrentDay = isToday(day);
    const maxEventsToShow = view === "month" ? 3 : 8;
    const hasMoreEvents = dayProjects.length > maxEventsToShow;
    const visibleProjects = dayProjects.slice(0, maxEventsToShow);

    return (
      <div
        className={cn(
          "min-h-[100px] overflow-hidden border p-1",
          !isCurrentMonth && "bg-gray-50 text-gray-400",
          isCurrentDay && "bg-blue-50"
        )}
      >
        <div className="mb-1 text-right">
          <span
            className={cn(
              "inline-block h-6 w-6 text-center text-sm",
              isCurrentDay && "rounded-full bg-blue-500 text-white"
            )}
          >
            {format(day, "d")}
          </span>
        </div>
        <div className="max-h-[80px] overflow-y-auto">
          {visibleProjects.map((project) => (
            <button
              key={project._id}
              onClick={() => onViewProject(project)}
              className={cn(
                getStatusBorderColor(project.status),
                "mb-1 w-full cursor-pointer rounded border-l-4 p-1 text-left text-xs hover:opacity-80"
              )}
              title={`${project.name} - ${project.locationName}`}
            >
              <div className="truncate font-medium">{project.name}</div>
              <div className="truncate text-gray-600">{project.locationName}</div>
            </button>
          ))}
          {hasMoreEvents && (
            <div className="mt-1 text-center text-xs text-gray-500">
              +{dayProjects.length - maxEventsToShow} more
            </div>
          )}
        </div>
      </div>
    );
  };

  // Render the calendar grid
  const renderCalendarGrid = () => {
    if (view === "month") {
      const monthStart = startOfMonth(currentDate);
      const monthStartDay = getDay(monthStart);
      const calendarDays: Date[] = [];

      // Add days from previous month
      for (let i = 0; i < monthStartDay; i++) {
        const day = addDays(monthStart, -monthStartDay + i);
        calendarDays.push(day);
      }

      // Add all days of the current month
      const daysInMonth = getDaysToDisplay();
      daysInMonth.forEach((day) => {
        calendarDays.push(day);
      });

      // Add days from next month to complete the grid
      const daysToAdd = 42 - calendarDays.length;
      for (let i = 1; i <= daysToAdd; i++) {
        const day = addDays(daysInMonth[daysInMonth.length - 1], i);
        calendarDays.push(day);
      }

      return (
        <div className="grid grid-cols-7">
          {calendarDays.map((day, index) => {
            const isCurrentMonth = isSameMonth(day, currentDate);
            return <div key={index}>{renderDayCell(day, isCurrentMonth)}</div>;
          })}
        </div>
      );
    } else {
      // Week view
      const days = getDaysToDisplay();
      return (
        <div className="grid grid-cols-7">
          {days.map((day, index) => {
            const isCurrentMonth = isSameMonth(day, currentDate);
            return <div key={index}>{renderDayCell(day, isCurrentMonth)}</div>;
          })}
        </div>
      );
    }
  };

  return (
    <Card className="w-full">
      <CardContent className="p-4">
        {renderHeader()}
        {renderHeaderRow()}
        {renderCalendarGrid()}
      </CardContent>
    </Card>
  );
};

export default ProjectCalendarView;
